import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import CustomButton from "../../../components/form/CustomButton";
import CustomInput from "../../../components/form/CustomInput";
import GoogleMapsStops from "../../../components/maps/GoogleMapsStops";
import StepTowForm from "./StepTwoForm";
import StopsList from "../StopsList";
import { useDispatch, useSelector } from "react-redux";
import { reset_stop, update_load } from "../../../store/loads/action";
import { ToastHelper } from "../../../helpers/configRoutes/alerts/alertHelper";

const StepTow = ({
    onNext,
    onBack,
    onCancel,
}) => {
    const dispatch = useDispatch();
    const { stop, validationsStop, load } = useSelector((state) => state.Load);

    const [newStop, setNewStop] = useState(false);
    const [stops, setStops] = useState(load.stops || []);

    // Toggle para millas manuales
    const [manualMilesEnabled, setManualMilesEnabled] = useState(false);
    const [manualMiles, setManualMiles] = useState("");

    const validationOrderStopsDate = () => {
        const previousStop = stops.find(
            (s) => s.order === validation.values.order - 1
        );

        if (!previousStop) return true;

        const { delivery_date, delivery_time } = previousStop;
        const { date, load_time } = validation.values;

        // Si falta algo, no bloqueamos aquí (Yup se encarga)
        if (!delivery_date || !delivery_time || !date || !load_time) {
            return true;
        }

        const previousDeliveryDateTime = buildDateTime(
            delivery_date,
            delivery_time
        );

        const currentPickupDateTime = buildDateTime(
            date,
            load_time
        );

        if (!previousDeliveryDateTime || !currentPickupDateTime) {
            return true;
        }

        // 🔥 Comparación real
        return currentPickupDateTime.getTime() > previousDeliveryDateTime.getTime();
    };


    const buildDateTime = (dateVal, timeStr) => {
        if (!dateVal || !timeStr) return null;

        // 1. Aseguramos que trabajamos con una copia de la fecha para no modificar la original
        const newDate = new Date(dateVal);

        // 2. Detectar si es PM o AM
        // Convertimos a minúsculas para evitar problemas con "P. M." vs "p. m."
        const timeLower = timeStr.toLowerCase();
        const isPM = timeLower.includes('p. m.') || timeLower.includes('pm');
        const isAM = timeLower.includes('a. m.') || timeLower.includes('am');

        // 3. Limpiar el string para obtener solo los números "06:10"
        // Esto elimina letras y espacios, dejando solo números y los dos puntos
        const cleanTime = timeStr.replace(/[^0-9:]/g, '');

        let [hours, minutes] = cleanTime.split(':').map(Number);

        // 4. Convertir a formato 24 horas para que JavaScript entienda
        if (isPM && hours < 12) hours += 12;
        if (isAM && hours === 12) hours = 0; // Las 12 AM son las 00 horas

        // 5. Asignar la hora a la fecha
        newDate.setHours(hours);
        newDate.setMinutes(minutes);
        newDate.setSeconds(0);
        newDate.setMilliseconds(0);

        return newDate;
    };

    const validation = useFormik({
        initialValues: stop,
        enableReinitialize: false,
        validationSchema: Yup.object(validationsStop),
        onSubmit: (values, { setFieldError }) => {
            values.order = stops.length + 1;


            const validaOrderDate = validationOrderStopsDate();


            if (validaOrderDate) {
                setStops([...stops, values]);
                dispatch(reset_stop());
                setNewStop(false);
                validation.resetForm();
            } else {
                setFieldError(
                    "date",
                    "Pickup must be after the previous stop delivery date and time"
                );
            }
        },
    });

    // millas prácticas calculadas automáticamente
    const autoPracticalMiles = stops
        .reduce((acc, s) => acc + (parseFloat(s.mile || 0) || 0), 0)
        .toFixed(2);

    const saveStops = () => {
        if (stops.length === 0) {
            ToastHelper(
                "You must add at least one stop before saving this load.",
                "error"
            );
            return false;
        }

        // Si el usuario activó manual, usamos su valor;
        // si no, usamos las calculadas
        const practicalMiles = manualMilesEnabled
            ? (parseFloat(manualMiles || 0) || 0).toFixed(2)
            : autoPracticalMiles;

        dispatch(
            update_load({
                ...load,
                stops: stops,
                practical_miles: practicalMiles,
            })
        );

        onNext();
    };

    const { values, errors, handleChange } = validation;

    // Valor que se muestra en el input
    const displayedMiles = manualMilesEnabled ? manualMiles : autoPracticalMiles;

    const handleToggleManualMiles = () => {
        setManualMilesEnabled((prev) => !prev);
        // Si se apaga el modo manual y no hay valor, dejamos el automático
        if (manualMilesEnabled) {
            // al desactivarlo, opcionalmente podrías limpiar manualMiles:
            // setManualMiles("");
        } else {
            // al activarlo, inicializamos con lo calculado para que tenga base
            setManualMiles(autoPracticalMiles);
        }
    };

    return (
        <>
            <div className="space-y-4">
                <h2 className="text-lg font-semibold text-cs-dark">Stops & route</h2>
                <p className="text-sm text-cs-muted">
                    Add pickup, delivery and intermediate stops, including addresses and schedule.
                </p>

                <div className="space-y-3">
                    {/* Mapa */}
                    <div className="grid grid-cols-1 gap-4 p-2">
                        <GoogleMapsStops
                            stops={stops}
                            setStops={setStops}
                            validation={validation}
                            newStop={newStop}
                        />
                    </div>

                    {/* Formulario / lista de stops */}
                    {newStop ? (
                        <StepTowForm validation={validation} setNewStop={setNewStop} />
                    ) : (
                        <div
                            className="
                w-full
                bg-cs-bg-alt
                border border-cs-border
                rounded-xl
                px-3 py-2
              "
                        >
                            <StopsList setNewStop={setNewStop} items={stops || []} />
                        </div>
                    )}

                    {/* Practical miles + toggle manual */}
                    <div className="mt-3 max-w-md space-y-2">
                        <div className="flex items-center justify-between gap-3">
                            <span className="text-xs font-medium text-cs-dark">
                                Practical miles
                            </span>

                            <button
                                type="button"
                                onClick={handleToggleManualMiles}
                                className={`
                  inline-flex items-center gap-1 px-2 py-1 rounded-full
                  border text-[11px]
                  ${manualMilesEnabled
                                        ? "bg-cs-primary text-white border-cs-primary"
                                        : "bg-cs-bg text-cs-muted border-cs-border"
                                    }
                `}
                            >
                                <span className="material-symbols-outlined text-[14px]">
                                    {manualMilesEnabled ? "edit" : "calculate"}
                                </span>
                                {manualMilesEnabled ? "Manual input" : "Auto calculated"}
                            </button>
                        </div>

                        <CustomInput
                            label=""
                            type="text"
                            name="practical_miles"
                            labelClassName="hidden"
                            error={undefined}
                            value={displayedMiles}
                            handleChange={
                                manualMilesEnabled
                                    ? (e) => setManualMiles(e.target.value)
                                    : () => { }
                            }
                            disabled={!manualMilesEnabled}
                        />

                        <p className="mt-1 text-[11px] text-cs-muted">
                            {manualMilesEnabled ? (
                                <>
                                    You are overriding the automatic miles. Current auto sum:{" "}
                                    <span className="font-semibold">{autoPracticalMiles}</span> mi.
                                </>
                            ) : (
                                <>
                                    Sum of miles between all stops in this load:{" "}
                                    <span className="font-semibold">{autoPracticalMiles}</span> mi.
                                </>
                            )}
                        </p>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mt-6 pt-4 border-t border-cs-border">
                <div className="text-xs text-cs-muted">
                    Step 2 of 3 – Configure stops and route.
                </div>
                <div className="flex justify-end gap-3">
                    <CustomButton
                        type="button"
                        onClick={onCancel}
                        className="
              inline-flex items-center gap-2
              px-4 py-2.5
              rounded-xl
              bg-cs-danger hover:bg-cs-primary-hover
              text-white text-sm font-medium
              shadow-md hover:shadow-lg
              transition-all
            "
                    >
                        Cancel
                    </CustomButton>

                    <CustomButton
                        type="button"
                        onClick={onBack}
                        className="
              inline-flex items-center gap-2
              px-4 py-2.5
              rounded-xl
              bg-cs-bg
              border border-cs-border
              text-sm text-cs-dark
              hover:bg-cs-bg-alt
              transition-all
            "
                    >
                        <span className="material-symbols-outlined text-sm">
                            arrow_back
                        </span>
                        Back
                    </CustomButton>

                    <CustomButton
                        type="button"
                        onClick={saveStops}
                        className="
              inline-flex items-center gap-2
              px-4 py-2.5
              rounded-xl
              bg-cs-primary hover:bg-cs-primary-hover
              text-white text-sm font-medium
              shadow-md hover:shadow-lg
              transition-all
            "
                    >
                        Next step
                        <span className="material-symbols-outlined text-sm">
                            arrow_forward
                        </span>
                    </CustomButton>
                </div>
            </div>
        </>
    );
};

export default StepTow;
